/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { Table } from 'rsuite-table';
import { toast } from 'react-toastify';

import { Form, Header, Icon, Dimmer, Loader } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import OPTIONS_TRIMESTRE from '@/constants/optionsTrimestre';
import { getFirstYear, getTrimestres, getDownloadPecoto } from '@/routes';
import type { Action, Tracabilite, Trimestre, Utilisateur } from '@/types';
import { useGestionTrimestre } from '@/hooks';

import ModalGestionTrimestre from '@/components/Dashboard/Blocs/ModalGestionTrimestre/ModalGestionTrimestre';
import ClotureConfirmationModal from '@/components/Dashboard/Blocs/ClotureConfirmationModal';

import 'rsuite-table/lib/less/index.less';

interface PeriodeRow {
  identifiant: number;
  identifiantPecoto?: number;
  identifiantEntite: number;
  identifiantLogin?: number;
  periode?: number;
  annee?: number;
  rowKey: string;
  description?: string;
  children?: PeriodeRow[];
  tracabilites?: Tracabilite[];
  child?: boolean;
  action?: Action;
  date?: string;
  utilisateur?: Utilisateur;
  dateNumerotation?: string;
  dateCloture?: string;
  dateEdition?: string;
  pecoto?: {
    date: string;
    telecharge: boolean;
  };
  isYear?: boolean;
  isTrimestre?: boolean;
  originalTrimestreData?: PeriodeRow;
}

const PeriodeTreeView = () => {
  const [data, setData] = React.useState<PeriodeRow[]>(null);
  const [firstYear, setFirstYear] = React.useState<number | null>(null);
  const [selectedYear, setSelectedYear] = React.useState<number | null>(null);
  const [selectedPeriod, setSelectedPeriod] = React.useState<number | null>(
    null,
  );
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<string[]>([]);
  const {
    states: {
      trimestre,
      loading,
      dateCloture,
      numerotation,
      generation,
      edition,
      telechargement,
      modalOpen,
      confirmationModalOpen,
    },
    actions: {
      setLoading,
      initializeTrimestre,
      handleNumerotation,
      handleGeneration,
      handleTelechargement,
      handleEdition,
      handleCloture,
      handleModalCloture,
      handleGestionTrimestreModalOpen,
      handleGestionTrimestreModalClose,
      setConfirmationModalOpen,
    },
  } = useGestionTrimestre();

  const generateChildren = (parent: PeriodeRow): PeriodeRow[] =>
    parent?.tracabilites.map((tracabilite, index) => ({
      rowKey: `${parent.identifiant}-child-${index + 1}`,
      child: true,
      ...tracabilite,
    }));

  React.useEffect(() => {
    request
      .get(getFirstYear())
      .then((response: Record<string, number>) => {
        setFirstYear(response.annee);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading]);

  // Reference to track if component is mounted
  const isMounted = React.useRef(true);

  // Prevent fetching if already loading
  const isLoadingRef = React.useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need generateChildren
  const fetchData = React.useCallback(() => {
    // Prevent multiple simultaneous requests
    if (isLoadingRef.current) return;

    // Fonction pour regrouper les données par année
    const groupDataByYear = (rawData: PeriodeRow[]): PeriodeRow[] => {
      // Grouper par année
      const groupedByYear = _.groupBy(rawData, 'annee');

      return _.map(groupedByYear, (trimestres, annee) => {
        // Créer une entrée pour l'année
        const yearRow: PeriodeRow = {
          identifiant: -parseInt(annee, 10), // ID négatif pour distinguer des vraies entrées
          identifiantEntite: trimestres[0].identifiantEntite,
          annee: parseInt(annee, 10),
          rowKey: `year-${annee}`,
          isYear: true,
          children: trimestres.map((trimestreItem) => ({
            ...trimestreItem,
            rowKey: `trimestre-${annee}-${trimestreItem.periode}`,
            isTrimestre: true,
            originalTrimestreData: trimestreItem,
            children: generateChildren(trimestreItem),
          })),
        };

        return yearRow;
      }).sort((a, b) => (b.annee || 0) - (a.annee || 0)); // Trier par année décroissante
    };

    isLoadingRef.current = true;
    setLoading(true);

    request
      .get(getTrimestres())
      .then((response: PeriodeRow[]) => {
        // Check if component is still mounted before updating state
        if (!isMounted.current) return;

        if (_.isArray(response)) {
          // Utiliser la nouvelle fonction pour grouper par année
          const groupedData = groupDataByYear(response);
          setData(groupedData);

          // permet de déterminer la période à ouvrir par défaut
          if (groupedData.length > 0) {
            // Ouvrir l'année la plus récente par défaut
            const mostRecentYear = groupedData[0]; // déjà trié par année décroissante
            const expandedKeys = [mostRecentYear.rowKey];

            // Ouvrir aussi le trimestre le plus récent de cette année
            if (mostRecentYear.children && mostRecentYear.children.length > 0) {
              const lastTrimestre = _.maxBy(
                mostRecentYear.children,
                (item) => item.periode || 0,
              );
              if (lastTrimestre) {
                expandedKeys.push(lastTrimestre.rowKey);
              }
            }

            setExpandedRowKeys(expandedKeys);
          }
        } else {
          toast('Une erreur est survenue lors de la récupération des données', {
            type: 'error',
          });
        }
      })
      .catch(() => {
        if (isMounted.current) {
          toast('Une erreur est survenue lors de la récupération des données', {
            type: 'error',
          });
        }
      })
      .finally(() => {
        if (isMounted.current) {
          setLoading(false);
        }
        isLoadingRef.current = false;
      });
  }, [setLoading]);

  // Initial data fetch
  React.useEffect(() => {
    fetchData();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted.current = false;
    };
  }, [fetchData]);

  const handleChangeYear = React.useCallback(
    (_e: React.SyntheticEvent, { value }) => {
      setSelectedYear(value);
      setSelectedPeriod(null);
    },
    [],
  );

  const handleChangePeriod = React.useCallback(
    (_e: React.SyntheticEvent, { value }) => {
      setSelectedPeriod(value);
    },
    [],
  );

  const handleResetFilter = React.useCallback(() => {
    setSelectedYear(null);
    setSelectedPeriod(null);
  }, []);

  // à voir si il sera possible de generer depuis la liste
  // const handleUploadGenerationPecoto = (annee: number, periode: number) => {
  //   request
  //     .post(uploadGenerationPecoto(annee, periode))
  //     .then(() => {
  //       toast(`Fichier généré pour la
  // ${periode}${periode === 1 ? 'ère' : 'ème'} période de l'année ${annee} `, { type: 'success' });
  //     })
  //     .catch(() => {
  //       toast('Une erreur est survenue lors de la génération du fichier', { type: 'error' });
  //     });
  // };

  const handleDownloadPecoto = (rowData: PeriodeRow) => {
    // Pour la nouvelle structure à 3 niveaux, trouver le trimestre parent
    let trimestreParent: PeriodeRow | undefined;

    // Utiliser find et some pour parcourir la structure
    data.some((yearRow) => {
      if (yearRow.children) {
        return yearRow.children.some((trimestreRow) => {
          if (trimestreRow.children) {
            const foundAction = trimestreRow.children.find(
              (child) => child.rowKey === rowData.rowKey,
            );
            if (foundAction) {
              trimestreParent =
                trimestreRow.originalTrimestreData || trimestreRow;
              return true; // Arrêter la recherche
            }
          }
          return false;
        });
      }
      return false;
    });

    if (trimestreParent?.identifiantPecoto) {
      request
        .get(getDownloadPecoto(trimestreParent.identifiantPecoto), {
          responseType: 'blob',
          Accept: '*/*',
        })
        .then((d: Blob) => {
          const url = URL.createObjectURL(d);
          const link = document.createElement('a');
          link.href = url;
          link.download = `RE_${trimestreParent.annee}${trimestreParent.periode}.txt`;
          link.click();
        });
    }
  };

  const getNewestTelechargement = () => {
    let highestTelChild = null;

    data?.forEach((yearRow) => {
      if (yearRow.children) {
        yearRow.children.forEach((trimestreRow) => {
          if (trimestreRow.children && trimestreRow.children.length > 0) {
            const lastTel = _.maxBy(
              trimestreRow.children.filter(
                (child) => child.action?.code === 'TEL',
              ),
              'identifiant',
            );

            const lastGen = _.maxBy(
              trimestreRow.children.filter(
                (child) => child.action?.code === 'GEN',
              ),
              'identifiant',
            );

            if (
              lastTel &&
              trimestreRow.originalTrimestreData?.identifiantPecoto &&
              (!lastGen || lastTel.identifiant > lastGen.identifiant)
            ) {
              if (
                !highestTelChild ||
                lastTel.identifiant > highestTelChild.identifiant
              ) {
                highestTelChild = lastTel;
              }
            }
          }
        });
      }
    });

    return highestTelChild;
  };

  const newestTelechargement = getNewestTelechargement();

  const currentYear = new Date().getFullYear();
  const yearOptions = React.useMemo(() => {
    if (!firstYear) return [];

    const years = [{ key: 0, text: '', value: null }]; // permet de quitter les filtres sans séléctionner de valeur

    for (let i = firstYear; i <= currentYear; i += 1) {
      years.push({ key: i, text: i.toString(), value: i });
    }

    return years;
  }, [currentYear, firstYear]);

  const filteredData = React.useMemo(() => {
    if (!selectedYear && !selectedPeriod) return data;

    return data
      ?.map((yearRow) => {
        // Si on filtre par année et que cette année ne correspond pas, exclure toute l'année
        if (selectedYear && yearRow.annee !== selectedYear) {
          return null;
        }

        // Si on filtre par période, filtrer les trimestres de cette année
        if (selectedPeriod && yearRow.children) {
          const filteredTrimestres = yearRow.children.filter(
            (trimestreItem) => trimestreItem.periode === selectedPeriod,
          );

          // Si aucun trimestre ne correspond, exclure l'année
          if (filteredTrimestres.length === 0) {
            return null;
          }

          // Retourner l'année avec seulement les trimestres qui correspondent
          return {
            ...yearRow,
            children: filteredTrimestres,
          };
        }

        return yearRow;
      })
      .filter(Boolean) as PeriodeRow[]; // Enlever les null
  }, [data, selectedYear, selectedPeriod]);

  const handleOpenModal = (rowData: PeriodeRow) => {
    // Pour les lignes trimestre, utiliser les données originales
    const dataToUse = rowData.originalTrimestreData || rowData;
    // Convert rowData to TrimestrePartial with explicit type assertion
    initializeTrimestre(dataToUse as unknown as Partial<Trimestre>);
    handleGestionTrimestreModalOpen();
  };

  // This function prevents excessive calls to fetchData
  const debouncedFetchRef = React.useRef(
    _.debounce(() => {
      if (isMounted.current && !isLoadingRef.current) {
        fetchData();
      }
    }, 300),
  );

  const handleModalActionComplete = React.useCallback(() => {
    if (isMounted.current && !isLoadingRef.current) {
      fetchData();
      debouncedFetchRef.current();
    }
  }, [fetchData]);

  const renderConfirmCloture = React.useMemo(
    () => (
      <ClotureConfirmationModal
        loading={loading}
        isClotured={!_.isNil(dateCloture)}
        onClose={() => setConfirmationModalOpen(false)}
        onConfirm={() => {
          handleCloture().then((success) => {
            if (success) handleModalActionComplete();
          });
        }}
      />
    ),
    [
      loading,
      dateCloture,
      handleCloture,
      setConfirmationModalOpen,
      handleModalActionComplete,
    ],
  );

  return (
    <>
      <Header>Liste des trimestres de recensement</Header>
      <Form.Group
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          margin: '.5em 0',
          flexWrap: 'wrap',
        }}
      >
        <label>Filtrer sur :&nbsp;</label>
        <Form.Dropdown
          selection
          options={yearOptions}
          value={selectedYear}
          onChange={handleChangeYear}
          placeholder="Année"
          style={{ minWidth: 100 }}
          data-testid="year-dropdown"
        />
        <label>-</label>
        <Form.Dropdown
          selection
          options={[{ key: 0, text: '', value: null }, ...OPTIONS_TRIMESTRE]}
          value={selectedPeriod}
          onChange={handleChangePeriod}
          disabled={!selectedYear}
          placeholder="Trimestre"
          style={{ minWidth: 150 }}
          data-testid="period-dropdown"
        />
        {selectedYear && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: need
          <span
            onClick={handleResetFilter}
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              fontWeight: 'bold',
            }}
            data-testid="reset-filter"
          >
            Supprimer les filtres
          </span>
        )}
      </Form.Group>

      <Table
        isTree
        rowKey="rowKey"
        hover
        cellBordered
        locale={{
          emptyMessage:
            "Nous n'avons trouvé aucun résultat correspondant aux critères de votre recherche.",
          loading: 'Chargement de données ...',
        }}
        data={filteredData || []}
        loading={loading || _.isNil(data)}
        fillHeight
        affixHeader
        showHeader
        virtualized
        rowHeight={40}
        expandedRowKeys={expandedRowKeys}
        onExpandChange={(expanded, rowData) => {
          if (expanded) {
            setExpandedRowKeys([...expandedRowKeys, rowData?.rowKey]);
          } else {
            setExpandedRowKeys(
              expandedRowKeys.filter((key) => key !== rowData?.rowKey),
            );
          }
        }}
        onRowClick={(rowData) => {
          if (rowData?.children?.length) {
            if (expandedRowKeys.includes(rowData.rowKey)) {
              setExpandedRowKeys(
                expandedRowKeys.filter((key) => key !== rowData.rowKey),
              );
            } else {
              setExpandedRowKeys([...expandedRowKeys, rowData.rowKey]);
            }
          }
        }}
        rowClassName={(rowData) =>
          rowData?.children?.length ? 'trimestre-row-clickable' : ''
        }
        renderTreeToggle={(expandButton, rowData) =>
          rowData.children?.length ? expandButton : null
        }
      >
        {({ Cell, Column, HeaderCell }) => (
          <>
            <Column flexGrow={1} verticalAlign="middle">
              <HeaderCell>
                <span style={{ fontWeight: 'bold' }}>
                  Trimestre de recensement
                </span>
              </HeaderCell>
              <Cell>
                {(rowData: PeriodeRow) => {
                  if (rowData?.isYear) {
                    return `Année ${rowData?.annee}`;
                  }
                  if (rowData?.isTrimestre) {
                    return `${rowData?.periode === 1 ? `${rowData?.periode}er trimestre` : `${rowData?.periode}ème trimestre`}`;
                  }
                  // Actions (niveau enfant)
                  return rowData?.action?.libelle;
                }}
              </Cell>
            </Column>
            <Column flexGrow={1} verticalAlign="middle">
              <HeaderCell>
                <span style={{ fontWeight: 'bold' }}>Fait le</span>
              </HeaderCell>
              <Cell>
                {(rowData: PeriodeRow) => {
                  // Afficher la date seulement pour les actions (niveau enfant)
                  if (
                    !rowData?.isYear &&
                    !rowData?.isTrimestre &&
                    rowData?.child &&
                    rowData?.date
                  ) {
                    return new Date(rowData.date).toLocaleDateString();
                  }
                  return null;
                }}
              </Cell>
            </Column>
            <Column flexGrow={1} verticalAlign="middle">
              <HeaderCell>
                <span style={{ fontWeight: 'bold' }}>
                  Par l&apos;utilisateur
                </span>
              </HeaderCell>
              <Cell>
                {(rowData: PeriodeRow) => {
                  // Afficher l'utilisateur seulement pour les actions (niveau enfant)
                  if (
                    !rowData.isYear &&
                    !rowData.isTrimestre &&
                    rowData.child === true
                  ) {
                    return _.isNil(rowData?.identifiantLogin)
                      ? 'Traitement automatique'
                      : _.isEqual(rowData?.identifiantLogin, 99999)
                        ? 'Reprise de données'
                        : `${rowData?.utilisateur?.nom} ${rowData?.utilisateur?.prenom}`;
                  }
                  return null;
                }}
              </Cell>
            </Column>
            <Column verticalAlign="middle" align="center" width={70}>
              <HeaderCell>
                <span />
              </HeaderCell>
              <Cell>
                {(rowData: PeriodeRow) => {
                  // Bouton d'édition pour les trimestres (niveau intermédiaire)
                  if (
                    rowData.isTrimestre &&
                    rowData.children &&
                    rowData.children.length > 0
                  ) {
                    return (
                      <Icon
                        name="edit"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleOpenModal(rowData)}
                        data-testid="edit-icon"
                      />
                    );
                  }
                  // Bouton de téléchargement pour l'action la plus récente
                  if (
                    !rowData.isYear &&
                    !rowData.isTrimestre &&
                    rowData.rowKey === newestTelechargement?.rowKey
                  ) {
                    return (
                      <Icon
                        name="download"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleDownloadPecoto(rowData)}
                        data-testid="download-icon"
                      />
                    );
                  }
                  return null;
                }}
              </Cell>
            </Column>
          </>
        )}
      </Table>

      {modalOpen && trimestre && (
        <ModalGestionTrimestre
          trimestre={trimestre.periode}
          annee={trimestre.annee}
          numerotation={numerotation}
          generation={generation}
          edition={edition}
          dateCloture={dateCloture}
          telechargement={telechargement}
          loading={loading}
          onNumerotation={(action) => {
            handleNumerotation(action).then((success) => {
              if (success) handleModalActionComplete();
            });
          }}
          onGeneration={() => {
            handleGeneration().then((success) => {
              if (success) handleModalActionComplete();
            });
          }}
          onTelechargement={() => {
            handleTelechargement().then((success) => {
              if (success) handleModalActionComplete();
            });
          }}
          onEdition={(d) => {
            handleEdition(d).then((success) => {
              if (success) handleModalActionComplete();
            });
          }}
          onCloture={handleModalCloture}
          onClose={handleGestionTrimestreModalClose}
        />
      )}

      {confirmationModalOpen && renderConfirmCloture}
      {loading && (
        <Dimmer active inverted>
          <Loader />
        </Dimmer>
      )}
    </>
  );
};

export default PeriodeTreeView;
